// Job: Maps the hostname to marketing or dashboard and builds cross-site links that stay in the same environment (prod or preview).
// Why: One domain serves both sites; without this, preview marketing links would send hosts to the prod dashboard.
// Hostname → site:
//   hello.stayatlas.com.au, hello-preview.stayatlas.com.au, www.* → marketing
//   stayatlas.com.au (apex), preview.stayatlas.com.au → dashboard
// Local dev: marketing by default; dashboard via ?app=1.
//
// "Preview environment" = hostnames carrying the preview Vercel deploy +
// preview Fly API. Login/dashboard links from hello-preview must point at
// preview.stayatlas.com.au, NOT the prod apex.

type Site = 'marketing' | 'app';

const MARKETING_HOSTS = new Set([
  'hello.stayatlas.com.au',
  'hello-preview.stayatlas.com.au',
  'www.stayatlas.com.au',
]);
const APP_HOSTS = new Set(['stayatlas.com.au', 'preview.stayatlas.com.au']);
const PREVIEW_HOSTS = new Set([
  'preview.stayatlas.com.au',
  'hello-preview.stayatlas.com.au',
]);

function isPreviewHost(host: string): boolean {
  return PREVIEW_HOSTS.has(host);
}

// why: main.tsx picks the marketing or dashboard router from this
export function detectSite(): Site {
  if (typeof window === 'undefined') return 'marketing';
  const host = window.location.hostname;
  if (MARKETING_HOSTS.has(host) || host.startsWith('hello.') || host.startsWith('hello-') || host.startsWith('www.')) return 'marketing';
  if (APP_HOSTS.has(host)) return 'app';
  if (new URLSearchParams(window.location.search).get('app') === '1') return 'app';
  return 'marketing';
}

// why: marketing sign-in links must land on the dashboard of the same environment
export function appUrl(): string {
  if (typeof window === 'undefined') return 'https://stayatlas.com.au';
  const host = window.location.hostname;
  const protocol = window.location.protocol;
  if (isPreviewHost(host)) return `${protocol}//preview.stayatlas.com.au`;
  if (host.endsWith('stayatlas.com.au')) return `${protocol}//stayatlas.com.au`;
  return window.location.origin;
}

export function marketingUrl(): string {
  if (typeof window === 'undefined') return 'https://hello.stayatlas.com.au';
  const host = window.location.hostname;
  const protocol = window.location.protocol;
  if (isPreviewHost(host)) return `${protocol}//hello-preview.stayatlas.com.au`;
  if (host.endsWith('stayatlas.com.au')) return `${protocol}//hello.stayatlas.com.au`;
  return `${window.location.origin}/`;
}
